import { useEffect, useMemo, useState } from 'react';
import { Select, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
const { Option } = Select;
import organizationEventEmitter from '../../../../../../../utilities/organizationEmmiter';
import { getFromStorage } from '../../../../../../../lib/storage';
import { listAgentsAction } from 'src/modules/SuperAdmin/store/actions/agents.action';

const AgentsPromptsFilters = ({ filters, setFilters }) => {
  const dispatch = useDispatch();
  const { currentPractitioner, agentsList, agentsLoading } = useSelector(
    (state) => ({
      currentPractitioner: state?.practitionerState?.currentPractitioner,
      agentsList: state?.agentsState?.agentsList,
      agentsLoading: state?.agentsState?.agentsLoading,
    })
  );

  const { promptScreens, promptScreensLoading } = useSelector(
    (state) => state?.promptsState
  );

  const [localSelectedOrganizationUUID, setLocalSelectedOrganizationUUID] =
    useState(() => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    });

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setLocalSelectedOrganizationUUID(newOrg?.org_uuid);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (localSelectedOrganizationUUID) {
      setFilters({
        ...filters,
        agent_id: undefined,
      });
      dispatch(listAgentsAction(localSelectedOrganizationUUID));
    }
  }, [localSelectedOrganizationUUID, dispatch]);

  const filteredAgentsList = useMemo(() => {
    if (!agentsList?.agents || !Array.isArray(agentsList?.agents)) return [];
    return agentsList.agents;
  }, [agentsList]);

  return (
    <Space style={{ marginBottom: '16px' }} wrap>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="bg-white shadow-sm rounded-xl w-full sm:w-48 md:w-52 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Agent"
            onChange={(value) => setFilters({ ...filters, agent_id: value })}
            value={filters.agent_id}
            loading={agentsLoading}
            variant="borderless"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={filteredAgentsList?.map((agent) => ({
              label: agent.name,
              value: agent.agent_id,
            }))}
          />
        </div>

        <div className="bg-white shadow-sm rounded-xl w-full sm:w-64 md:w-72 lg:w-80 flex items-center">
          <Select
            className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
            placeholder="Select Prompt Screen"
            onChange={(value) =>
              setFilters({ ...filters, prompt_screens_id: value })
            }
            value={filters.prompt_screens_id}
            loading={promptScreensLoading}
            variant="borderless"
            allowClear
            showSearch
            optionLabelProp="label"
            filterOption={(input, option) => {
              const children = option.children;
              const textContent =
                children?.props?.children?.[0]?.props?.children || '';
              const descContent =
                children?.props?.children?.[1]?.props?.children || '';
              return (
                textContent.toLowerCase().includes(input.toLowerCase()) ||
                descContent.toLowerCase().includes(input.toLowerCase())
              );
            }}
          >
            {promptScreens?.prompt_screens?.map((screen) => (
              <Option
                key={screen.prompt_screens_id}
                value={screen.prompt_screens_id}
                label={screen.description || screen.prompt_screen}
              >
                <div className="flex flex-col">
                  <div className="font-medium">
                    {screen.description || screen.prompt_screen}
                  </div>
                  {screen.description && (
                    <div className="text-xs text-gray-500">
                      {screen.prompt_screen}
                    </div>
                  )}
                </div>
              </Option>
            ))}
          </Select>
        </div>

        <div className="flex items-center">
          <span
            onClick={() => {
              setFilters({ page: 1, per_page: 20, has_agent: true });
            }}
            className="text-sm cursor-pointer hover:underline whitespace-nowrap"
          >
            Clear
          </span>
        </div>
      </div>
    </Space>
  );
};

export default AgentsPromptsFilters;
